import type { ReactElement } from 'react';
import type { InspectorTab } from '../lib/inspector';
import type { SectionId } from '../lib/sections';

/**
 * The navigation and tool icons that share a meaning with the native app (#799).
 *
 * The native app's places carry the clearer picture of what each page is for, so
 * where the web has the same destination or tool it draws the same idea - in its
 * own plain strokes, never the native artwork. The native symbol each one stands in
 * for is named beside it. The icon itself is always decoration: the button or
 * tab around it carries the name a screen reader reads.
 */

export type NavIconName =
  | 'mainCommand' // bubble.left.and.bubble.right
  | 'operations' // gauge.with.dots.needle.33percent
  | 'workBoard' // rectangle.split.3x1
  | 'roadmap' // map
  | 'machines' // desktopcomputer
  | 'notes' // note.text
  | 'settings'; // no native place; the web's own gear

const GLYPHS: Readonly<Record<NavIconName, ReactElement>> = {
  // Two speech bubbles: a conversation, not a single message.
  mainCommand: (
    <>
      <path d="M5.5 8.5h8a2 2 0 0 1 2 2v4.5a2 2 0 0 1-2 2H9.5L6 20v-3h-.5a2 2 0 0 1-2-2v-4.5a2 2 0 0 1 2-2z" />
      <path d="M8.5 8.5V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4.5a2 2 0 0 1-2 2H18V15l-2.5-2.1" />
    </>
  ),
  // A round gauge with its dots and a needle a third of the way round.
  operations: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M7.2 14h.01M8.4 10h.01M12 8.2h.01M15.6 10h.01M16.8 14h.01" />
      <path d="M12 14 9.6 10.9" />
    </>
  ),
  // One board split into three columns.
  workBoard: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M9 5v14M15 5v14" />
    </>
  ),
  // A folded map.
  roadmap: (
    <>
      <path d="M3.5 6.5 9 4.5l6 2 5.5-2v13l-5.5 2-6-2-5.5 2z" />
      <path d="M9 4.5v13M15 6.5v13" />
    </>
  ),
  // A desktop computer on its stand.
  machines: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M3 13h18M12 16v3.5M8.5 19.5h7" />
    </>
  ),
  // A note with lines of text.
  notes: (
    <>
      <rect x="4.5" y="3.5" width="15" height="17" rx="2" />
      <path d="M8 8.5h8M8 12h8M8 15.5h5" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v2.2M12 18.3v2.2M20.5 12h-2.2M5.7 12H3.5M18 6l-1.6 1.6M7.6 16.4 6 18M18 18l-1.6-1.6M7.6 7.6 6 6" />
    </>
  ),
};

/**
 * The rail's rooms. The Control Center is where the owner talks to Brunel (the
 * native Main Command); the Engine Room is read off gauges (the native Operations).
 */
export const SECTION_ICON: Readonly<Record<SectionId, NavIconName>> = {
  control: 'mainCommand',
  engine: 'operations',
  settings: 'settings',
};

/** Right-drawer tools that have a native equivalent; the rest keep their own icons. */
export const TOOL_ICON: Readonly<Partial<Record<InspectorTab, NavIconName>>> = {
  notes: 'notes',
  workboard: 'workBoard',
  roadmap: 'roadmap',
  machines: 'machines',
};

export function NavIcon({ name, strokeWidth, size }: {
  readonly name: NavIconName;
  readonly strokeWidth: number;
  /** Fixed pixel size; left out, the surrounding stylesheet sizes it. */
  readonly size?: number;
}): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false" data-icon={name}>
      {GLYPHS[name]}
    </svg>
  );
}
